'use client';

import { PropsWithClassname } from '@/app/types';
import classNames from 'classnames';
import { useMemo } from 'react';

import { Trans, useTranslation } from 'react-i18next';

import Sticky from 'react-sticky-el';

import { CreateDonationRequestModeEnum, DonationMatching, Subproject } from '@/app/api';

import Button, { ButtonHTMLTag, ButtonStyle } from '@/app/components/Button/Button';
import CheckIcon from '@/app/components/CheckIcon/CheckIcon';
import { MatchingBox } from '@/app/components/MatchingBox/MatchingBox';
import TabsSelect, { TabsSelectOption } from '@/app/components/TabsSelect/TabsSelect';
import { formatPriceInCents } from '@/app/utils/formatting';

interface DonateBoxProps extends PropsWithClassname {
  donateLink: string;
  selectedDonationMode: CreateDonationRequestModeEnum;
  onChangeSelectedDonationMode: (nextDonationMode: CreateDonationRequestModeEnum) => void;
  donationModeOptions: TabsSelectOption<CreateDonationRequestModeEnum>[];
  isProjectLongTerm: boolean;
  onShareButtonClick: () => void;
  onVerificationNoteClick: () => void;
  subProject?: Subproject;
  activeGoodHabitAmount?: number;
  matching?: DonationMatching;
}

const DonateBoxDobro = ({
  donateLink,
  donationModeOptions,
  selectedDonationMode,
  isProjectLongTerm,
  onChangeSelectedDonationMode,
  onShareButtonClick,
  onVerificationNoteClick,
  className,
  subProject,
  activeGoodHabitAmount,
  matching,
}: DonateBoxProps) => {
  const { t } = useTranslation();

  const stickyContent = useMemo(
    () => (
      <div className={classNames('flex lg:flex-col py-[20px] lg:py-[12px]')}>
        <Button
          style={ButtonStyle.PRIMARY}
          className="grow text-center mr-[16px] lg:mr-0 lg:mb-[16px] lg:px-[24px] lg:py-[20px]"
          tagSpecificProps={{ htmlTag: ButtonHTMLTag.LINK, href: donateLink }}
        >
          {t(activeGoodHabitAmount ? 'components.DonateBox.donateAmount' : 'components.DonateBox.donate', {
            amount: formatPriceInCents(activeGoodHabitAmount ?? 0),
          })}
        </Button>
        <Button
          style={ButtonStyle.PRIMARY_OUTLINED}
          className="grow-0 lg:px-[24px] lg:py-[20px]"
          tagSpecificProps={{ htmlTag: ButtonHTMLTag.BUTTON, onClick: onShareButtonClick }}
        >
          {t('components.DonateBox.share')}
        </Button>
      </div>
    ),
    [donateLink, onShareButtonClick, t],
  );

  return (
    <div
      className={classNames(
        className,
        'lg:bg-white lg:rounded-[4px] lg:border lg:border-neutral-light lg:shadow-md lg:py-[32px] lg:px-[32px]',
      )}
    >
      <h2 className="hidden lg:inline-block text-center text-[20px] font-medium leading-[24px] text-balance mt-0">
        {t('components.DonateBox.heading')}
      </h2>
      {isProjectLongTerm && !subProject && (
        <TabsSelect<CreateDonationRequestModeEnum>
          value={selectedDonationMode}
          onChange={onChangeSelectedDonationMode}
          options={donationModeOptions}
          className="my-[16px]"
        />
      )}
      <div className="block lg:hidden">
        <Sticky stickyClassName="!w-full left-0 px-[24px] shadow-lg bg-white z-20">{stickyContent}</Sticky>
      </div>
      <div className="hidden lg:block">{stickyContent}</div>
      {!subProject && (
        <div className="text-center">
          <CheckIcon className="text-success-mid inline w-[24px] mr-[4px]" />
          <Trans i18nKey="components.DonateBox.verification">
            Všechny sbírky{' '}
            <button
              className="inline underline hover:text-primary-mid hover:no-underline"
              onClick={onVerificationNoteClick}
            >
              ověřujeme
            </button>
          </Trans>
        </div>
      )}
      {matching && <MatchingBox matching={matching} />}
    </div>
  );
};

export default DonateBoxDobro;
